// NOTICE: KAPTION-INTEGRATION
// Feature Permissions Composable - Runtime gating helper for the client-managed
// feature permission layer.
//
// This composable reads the per-feature EFFECTIVE access (feature flag AND
// client-configured permission already combined by the backend) from the
// account record loaded at bootstrap, without having to pass accountId every time.
//

import { useStore } from 'vuex';
import { useAccount } from './useAccount';

/**
 * Composable for checking client-managed feature permissions
 *
 * @returns {Object} Object containing helper functions
 *
 * @example
 * import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
 *
 * const { canUseFeature } = useFeaturePermissions();
 *
 * if (canUseFeature('scheduled_messages')) {
 *   // The current user is permitted to use this feature
 * }
 */
export function useFeaturePermissions() {
  const store = useStore();
  const { accountId } = useAccount();

  /**
   * Check if the current user is permitted to use a feature on this account.
   *
   * Fail-open: if the permissions map is absent on the account record, the
   * underlying getter returns true so a missing payload never hides the UI.
   *
   * @param {string} featureKey - The feature key (e.g. 'scheduled_messages')
   * @returns {boolean} True if the feature is permitted, false otherwise
   */
  const canUseFeature = featureKey => {
    if (!accountId.value) {
      return true;
    }

    return store.getters['accounts/isFeaturePermittedonAccount'](
      accountId.value,
      featureKey
    );
  };

  return {
    canUseFeature,
  };
}
